import { langText } from '@fs/zion-locale'

/**
 * Gets the full language name from a language code
 * @param {string} langCode - The language code (e.g., 'en', 'es', 'ja')
 * @returns {string} The full language name (e.g., 'English', 'Español', '日本語') or the code if not found
 */
export const getLanguageName = (langCode) => {
  const allLanguages = [...langText.core, ...langText.additional]
  const language = allLanguages.find((lang) => lang.code === langCode)
  return language ? language.text : langCode
}

/**
 * Helper: Returns appropriate tooltip message for translation toggle based on various conditions.
 * If translation is available, no tooltip is needed.
 * If there are errors or no transcript text, also no tooltip is shown.
 * The default case is to show a message about language settings.
 *
 * @param {Object} params - The parameters object
 * @param {boolean} params.isTranslationAvailable - Whether translation is available
 * @param {boolean} params.hasArtifactError - Whether there was an error loading the artifact
 * @param {boolean} params.hasTranslationError - Whether there was an error with the translation
 * @param {boolean} params.hasTranscriptText - Whether transcript text is present
 * @param {Function} params.t - Translation function for i18n
 * @returns {string|undefined} The tooltip message or undefined if no tooltip should be shown
 */
export const getDisabledTooltipMessage = ({
  isTranslationAvailable,
  hasArtifactError,
  hasTranslationError,
  hasTranscriptText,
  t,
}) => {
  // No tooltip needed if translation is available
  if (isTranslationAvailable) {
    return undefined
  }

  // Show generic message if there's an error or no transcript
  if (hasArtifactError || hasTranslationError || !hasTranscriptText) {
    return undefined
  }

  // Default message: languages match
  return t(
    'full-text.translation-disabled',
    'The transcription language matches your current language setting. To change the language, use the World icon in the FamilySearch header.'
  )
}
